/**
 * VidyaSvg — renders a ```vidya-svg``` fenced code block as a hand-drawn
 * SVG by re-stroking the curator's clean SVG output with svg2roughjs.
 *
 * Pipeline:
 *   1. Backend's figure_curator (Gemini 3 Flash) emits clean SVG from a
 *      semantic vidya-figure-spec.
 *   2. streaming_block_proxy.py sanitises the SVG and wraps it in a
 *      ```vidya-svg``` fence.
 *   3. MarkdownRenderer intercepts that fence and mounts this component.
 *   4. We parse the SVG string, pass it through svg2roughjs to apply the
 *      hand-drawn aesthetic (rough.js under the hood — same library
 *      VidyaFigure + MermaidDiagram use, so visual style matches).
 *
 * Interactions (mirroring PlotRenderer):
 *   • Inline preview is a click target — students see "⤢ Click to expand"
 *     on hover.
 *   • Click opens a fullscreen modal via React Portal to `document.body`
 *     (escapes any parent transform / will-change / filter that would
 *     trap a `position: fixed` modal inside the chat panel).
 *   • Esc OR clicking the backdrop closes the modal.
 *
 * Failure modes:
 *   • DOMParser fails on malformed SVG → graceful inline error.
 *   • svg2roughjs throws → same error path.
 *   • Empty body → "empty diagram" message.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  body: string;
}

// Lazy import — svg2roughjs + roughjs combined are ~150 KB gzipped. We
// only pay for them when an actual ```vidya-svg``` fence appears in
// the conversation.
let svg2roughjsPromise: Promise<typeof import('svg2roughjs')> | null = null;
function loadSvg2Roughjs() {
  if (!svg2roughjsPromise) {
    svg2roughjsPromise = import('svg2roughjs');
  }
  return svg2roughjsPromise;
}

/** Strip Unicode combining marks from the SVG source. The curator
 *  sometimes writes vector notation as "u" + U+20D7 (combining arrow
 *  above) inside <text>; macOS fonts render that as a striped fallback
 *  glyph ("gibberish" blocks). NFC first so decomposed accents collapse
 *  into precomposed glyphs (which render fine — î, θ, ĵ), then drop
 *  whatever combining marks remain (no precomposed form exists). */
function sanitizeSvgBody(body: string): string {
  return body.normalize('NFC').replace(/[\u0300-\u036F\u20D0-\u20FF]/g, '');
}

/** Widen the source viewBox when content overflows it (curator labels
 *  near the edge get clipped — e.g. an x-axis legend at x=365 in a
 *  400-wide viewBox). Measures real bounds off-screen; EXPAND-ONLY, so
 *  a well-formed SVG is untouched. Best-effort: on any failure the
 *  declared viewBox stays. */
function fitViewBoxToContent(svgEl: SVGSVGElement): void {
  const vb = svgEl.viewBox?.baseVal;
  if (!vb || vb.width <= 0 || vb.height <= 0) return;
  const holder = document.createElement('div');
  holder.style.cssText =
    'position:fixed;left:-10000px;top:0;width:1000px;visibility:hidden;';
  document.body.appendChild(holder);
  try {
    const clone = svgEl.cloneNode(true) as SVGSVGElement;
    clone.removeAttribute('width');
    clone.removeAttribute('height');
    holder.appendChild(clone);
    const bbox = clone.getBBox();
    if (!(bbox.width > 0 && bbox.height > 0)) return;
    // Padding covers rough.js stroke jitter + font differences between
    // this measurement and the sketched output.
    const PAD = 8;
    const x = Math.min(vb.x, bbox.x - PAD);
    const y = Math.min(vb.y, bbox.y - PAD);
    const right = Math.max(vb.x + vb.width, bbox.x + bbox.width + PAD);
    const bottom = Math.max(vb.y + vb.height, bbox.y + bbox.height + PAD);
    svgEl.setAttribute('viewBox', `${x} ${y} ${right - x} ${bottom - y}`);
  } catch {
    // getBBox can throw on detached/odd content in some browsers —
    // keep the declared viewBox.
  } finally {
    holder.remove();
  }
}

/** Parse + sketch the SVG body into the given target div, then PATCH
 *  the rendered output to be responsive.
 *
 *  svg2roughjs v3.2.3 has a known bug: its output SVG has pixel
 *  `width`/`height` attrs but NO `viewBox`. Modern browsers (Chrome /
 *  Firefox 2020+, per the SVG2 spec) require a `viewBox` to derive
 *  intrinsic aspect ratio for responsive sizing. Without it, CSS
 *  `width: 100%; height: auto` collapses to 0 height — the "thin
 *  strip" failure we hit.
 *
 *  Fix: copy the source viewBox onto the rendered output (or
 *  synthesise one from the width/height attrs), then strip the pixel
 *  attrs so CSS sizing wins. Now `width: 100%; height: auto` works
 *  exactly like an `<img>` with intrinsic dimensions. */
async function sketchInto(
  target: HTMLDivElement,
  body: string,
): Promise<SVGSVGElement> {
  const { Svg2Roughjs } = await loadSvg2Roughjs();
  const parser = new DOMParser();
  const doc = parser.parseFromString(sanitizeSvgBody(body), 'image/svg+xml');
  const parseErr = doc.querySelector('parsererror');
  if (parseErr) {
    throw new Error(parseErr.textContent?.split('\n')[0] || 'SVG parse error');
  }
  const svgEl = doc.documentElement as unknown as SVGSVGElement;
  if (!svgEl || svgEl.tagName.toLowerCase() !== 'svg') {
    throw new Error('Parsed element is not <svg>');
  }

  // Widen the viewBox if labels overflow it (expand-only), THEN remember
  // it — svg2roughjs sizes its output from the source viewBox but doesn't
  // propagate the attribute.
  fitViewBoxToContent(svgEl);
  const srcViewBox = svgEl.getAttribute('viewBox');

  const s2r = new Svg2Roughjs(target);
  s2r.fontFamily = 'system-ui, sans-serif';
  s2r.randomize = true;
  s2r.svg = svgEl;
  await s2r.sketch();

  const rendered = target.querySelector('svg') as SVGSVGElement | null;
  if (!rendered) throw new Error('svg2roughjs produced no SVG element');

  // 1. Ensure a viewBox exists. Prefer the source's; otherwise build
  //    one from the width/height attrs svg2roughjs set on the output.
  if (!rendered.hasAttribute('viewBox')) {
    if (srcViewBox) {
      rendered.setAttribute('viewBox', srcViewBox);
    } else {
      const w = rendered.getAttribute('width');
      const h = rendered.getAttribute('height');
      if (w && h) {
        rendered.setAttribute('viewBox', `0 0 ${w} ${h}`);
      }
    }
  }
  rendered.setAttribute('preserveAspectRatio', 'xMidYMid meet');

  // 2. Strip pixel width/height attrs so CSS controls sizing. With a
  //    viewBox present, the browser uses it to derive intrinsic aspect
  //    ratio, and `width: 100%; height: auto` scales the SVG to fit
  //    its container with the right shape.
  rendered.removeAttribute('width');
  rendered.removeAttribute('height');
  rendered.style.width = '100%';
  rendered.style.height = 'auto';
  rendered.style.maxWidth = '100%';
  rendered.style.display = 'block';
  // Belt-and-braces: an explicit aspect-ratio guarantees a non-zero
  // height even where the browser fails to derive one from the viewBox
  // (the "thin white strip" collapse).
  const vb = rendered.viewBox?.baseVal;
  if (vb && vb.width > 0 && vb.height > 0) {
    rendered.style.aspectRatio = `${vb.width} / ${vb.height}`;
  }

  return rendered;
}

export function VidyaSvg({ body }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const modalContainerRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleExpand = useCallback(() => setExpanded(true), []);
  const handleClose = useCallback(() => setExpanded(false), []);

  // Inline sketch — always rendered inside the chat answer.
  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;
    if (!container) return;

    const trimmed = body.trim();
    if (!trimmed) {
      setError('Empty SVG body');
      return;
    }

    setReady(false);
    setError(null);

    (async () => {
      try {
        // Sketch into a detached div, then swap in atomically. Overlapping
        // runs (React StrictMode double-effects, streaming body updates)
        // each own a private target, so a stale run can never blank or
        // double-fill the live container mid-flight.
        const target = document.createElement('div');
        await sketchInto(target, trimmed);
        if (cancelled) return;
        container.replaceChildren(...Array.from(target.childNodes));
        setReady(true);
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof Error ? err.message : String(err ?? 'unknown error');
        setError(message);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [body]);

  // Modal sketch — fresh re-sketch when expanded. svg2roughjs is fast
  // enough that re-running it for the modal is cheaper than cloning the
  // already-sketched SVG node and re-parenting it (which loses style
  // bindings).
  useEffect(() => {
    if (!expanded) return;
    let cancelled = false;
    const target = modalContainerRef.current;
    if (!target) return;

    while (target.firstChild) target.removeChild(target.firstChild);

    (async () => {
      try {
        const staging = document.createElement('div');
        await sketchInto(staging, body.trim());
        if (cancelled) return;
        target.replaceChildren(...Array.from(staging.childNodes));
        // No extra sizing here — the SVG carries its own aspect-ratio,
        // and the modal outer div has `overflow-auto` + `max-h-[95vh]`
        // so very tall diagrams scroll.
      } catch {
        // Modal sketch failed — the inline sketch already showed the
        // error so we don't need to surface this separately.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [expanded, body]);

  // Esc closes modal.
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded, handleClose]);

  if (error) {
    return (
      <div className="my-4 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm">
        <div className="font-medium text-amber-800 dark:text-amber-200 mb-2">
          Diagram couldn't be rendered.
        </div>
        <div className="text-xs text-amber-700 dark:text-amber-300">
          {error.slice(0, 200)}
        </div>
      </div>
    );
  }

  return (
    <>
      {/*
        Inline preview = clickable thumbnail. Same pattern as PlotRenderer:
        the whole panel is one click target that opens the fullscreen modal.
        Hover affordance shows "⤢ Click to expand". `pointer-events-none`
        on the inner container prevents text-selection drag from blocking
        the click.
      */}
      {/* `block` is critical — by default <button> is inline-block, which
          makes block descendants `width: auto` (shrink-to-fit). The inner
          SVG container then shrinks to the SVG's intrinsic 300×150 default
          (no width/height attrs on the SVG) instead of filling the chat
          column. `block w-full` forces the wrapper into block context so
          the SVG can fill 100%. */}
      <button
        type="button"
        onClick={handleExpand}
        disabled={!ready}
        className="group my-4 block w-full relative rounded-lg border border-slate-200
                   dark:border-slate-700 bg-white cursor-zoom-in
                   hover:border-slate-400 hover:shadow-md transition-all
                   disabled:cursor-default disabled:hover:border-slate-200 disabled:hover:shadow-none
                   p-0 text-left"
        aria-label="Click to expand diagram to fullscreen"
        title={ready ? 'Click to expand' : 'Rendering…'}
      >
        {/* Container is ALWAYS rendered (no `hidden` toggle) because
            svg2roughjs reads target.getBoundingClientRect() to scale
            output. The SVG inside sets its own intrinsic width/height
            (matching the source viewBox) so the container naturally
            takes the right shape — no aspect-ratio CSS needed. */}
        <div
          ref={containerRef}
          className="vidya-svg block w-full bg-white p-3 pointer-events-none select-none"
        />
        {/* Loading overlay — absolutely positioned so it doesn't change
            the container's size or layout; just fades over the empty
            container until svg2roughjs fills it. */}
        {!ready && (
          <div className="absolute inset-0 flex items-center justify-center text-xs text-slate-400 pointer-events-none">
            Rendering diagram…
          </div>
        )}
        {/* Hover overlay — "⤢ Click to expand" badge on hover. */}
        {ready && (
          <div className="absolute inset-0 flex items-center justify-center
                          bg-slate-900/0 group-hover:bg-slate-900/10
                          pointer-events-none transition-colors rounded-lg">
            <span className="opacity-0 group-hover:opacity-100 transition-opacity
                             bg-white border border-slate-300 text-slate-700
                             text-xs font-medium px-3 py-1.5 rounded shadow">
              ⤢&nbsp; Click to expand
            </span>
          </div>
        )}
      </button>

      {/*
        Modal rendered through a React Portal to `document.body`.
        Same reason as PlotRenderer: parents in the chat panel apply CSS
        transforms / will-change / filters that create a new containing
        block for `position: fixed`. Without the portal, the modal stays
        trapped inside the chat panel.
      */}
      {expanded &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-6"
            onClick={handleClose}
            role="dialog"
            aria-modal="true"
          >
            {/* Explicit width is required here. Without it, the modal
                shrinks to fit its content — but the SVG inside has
                `width: 100%`, which then resolves against the
                shrink-to-fit container, which resolves against the
                SVG's intrinsic 300×150 default. Result: a tiny SVG in
                a tiny modal. `w-[min(95vw,1200px)]` gives the modal a
                concrete width that the SVG can fill. */}
            <div
              className="relative bg-white rounded-lg shadow-2xl p-6 overflow-auto
                         w-[min(95vw,1200px)] max-h-[95vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={handleClose}
                className="absolute top-2 right-2 z-10 w-8 h-8 flex items-center justify-center
                           text-slate-500 hover:text-slate-900 hover:bg-slate-100
                           text-2xl leading-none rounded transition-colors"
                aria-label="Close fullscreen diagram"
                title="Close (Esc)"
              >
                ×
              </button>
              <div
                ref={modalContainerRef}
                className="mx-auto bg-white w-full"
              />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}

export default VidyaSvg;
